import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise} from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../../App/Services/AppService';
import CommandResult from '../../Common/Models/CommandResult';
import StaticServiceProviderFileGroupingModel from '../../Common/Models/StaticServiceProviderFileGroupingModel';
import TotalHoldingsLookup from '../../Home/Models/Lookups/TotalHoldingsLookup';
import SaveServiceProviderFileGroupingCommand from '../ServiceProviders/Models/Commands/SaveServiceProviderFileGroupingCommand';
import ServiceProviderFileGroupingLookup from '../ServiceProviders/Models/Lookups/ServiceProviderFileGroupingLookup';
import ServiceProviderSearchCriteria from '../ServiceProviders/Models/Queries/ServiceProviderSearchCriteria';
import SFTPBrokerDetailsLookup from '../ServiceProviders/Models/Lookups/SFTPBrokerDetailsLookup';
import UpdateSFTPDetailCommand from '../ServiceProviders/Models/Commands/UpdateSFTPBrokerDetailCommand';
import StaticServiceProviderModel from '../../Common/Models/StaticServiceProviderModel';
import AddSFTPDetailCommand from '../ServiceProviders/Models/Commands/AddSFTPBrokerDetailCommand';
import TradeDistributionTypeLookup from '../ServiceProviders/Models/Lookups/TradeDistributionTypeLookup';
import TradeSftpTypeLookup from '../ServiceProviders/Models/Lookups/TradeSftpTypeLookup';

export type LBVHLookupResponse = Model.PlainObject<ServiceProviderFileGroupingLookup> & {
    lastLBVHRun?: Date | string | null;
};

type ExecuteLBVHCommandResultBase = Omit<Model.PlainObject<CommandResult>, 'success' | 'data'> & {
    message?: string;
};

type ExecuteLBVHSuccessResult = ExecuteLBVHCommandResultBase & {
    success: true;
    data: LBVHLookupResponse;
};

type ExecuteLBVHFailureResult = ExecuteLBVHCommandResultBase & {
    success: false;
    data: null;
};

export type ExecuteLBVHCommandResult = ExecuteLBVHSuccessResult | ExecuteLBVHFailureResult;

export interface IServiceProviderApiClient {
    getPagedServiceProviderDetails(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<ServiceProviderSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ServiceProviderFileGroupingLookup>>>;
    getServiceProviderFileGroupingListByCriteria(criteria?: string): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderFileGroupingModel>>>;
    saveServiceProviderFileGrouping(command: Model.PlainObject<SaveServiceProviderFileGroupingCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    getSFTPBrokerDetails(id: number, criteria: string): AxiosPromise<Array<Model.PlainObject<SFTPBrokerDetailsLookup>>>
    setSFTPBrokerDetails(detials: Model.PlainObject<UpdateSFTPDetailCommand>) : AxiosPromise<Model.PlainObject<CommandResult>>;
    getServiceProviderListByCriteria(criteria: string): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderModel>>>;
    getServiceProviderFileGroupingLookupById(staticServiceProviderFileGroupingId: number): AxiosPromise<Model.PlainObject<ServiceProviderFileGroupingLookup>>;
    addSFTPBrokerDetails(details: Model.PlainObject<AddSFTPDetailCommand>) : AxiosPromise<Model.PlainObject<CommandResult>>;
    deleteSFTPBrokerDetails(staticServiceProviderSftpdetailsId: number): AxiosPromise<Model.PlainObject<CommandResult>>;
    executeLBVH(staticServiceProviderFileGroupingId: number): AxiosPromise<ExecuteLBVHCommandResult>;
}

@injectable()
export default class ServiceProviderApiClient extends Data.ApiClientBase implements IServiceProviderApiClient {
    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/StaticServiceProvider`)
    }

    public addSFTPBrokerDetails(command: Model.PlainObject<AddSFTPDetailCommand>) : AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/AddSFTPDetails`, command);
    }

    public getPagedServiceProviderDetails(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<ServiceProviderSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ServiceProviderFileGroupingLookup>>> {
        return this.axios.get(`${this.apiPath}/GetServiceProviderDetails?${Utils.getQueryString(payload)}`);
    }

    public saveServiceProviderFileGrouping(command: Model.PlainObject<SaveServiceProviderFileGroupingCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/SaveServiceProviderFileGrouping`, command);
    }

    public getServiceProviderFileGroupingListByCriteria(criteria: string = ""): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderFileGroupingModel>>> {
        return this.axios.get(`${this.apiPath}/GetServiceProviderFileGroupingListByCriteria`, { params: { criteria: criteria } });
    }

    public getTotalHoldings(): AxiosPromise<Array<Model.PlainObject<TotalHoldingsLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTotalHoldings`);
    }

    public getSFTPBrokerDetails(StaticServiceProviderFileGroupingID: number): AxiosPromise<Array<Model.PlainObject<SFTPBrokerDetailsLookup>>> {
        return this.axios.get(`${this.apiPath}/GetSFTPDetails`, {params: {StaticServiceProviderFileGroupingID: StaticServiceProviderFileGroupingID}});
    }

    public setSFTPBrokerDetails(command: Model.PlainObject<UpdateSFTPDetailCommand>) : AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/UpdateSFTPDetails`, command);
    }

    public deleteSFTPBrokerDetails(staticServiceProviderSftpdetailsId: number): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.delete(`${this.apiPath}/DeleteSFTPDetails`, { params: { staticServiceProviderSftpdetailsId } });
    }

    public getServiceProviderListByCriteria(criteria: string): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderModel>>> {
        return this.axios.get(`${this.apiPath}/GetServiceProviderListByCriteria`, { params: { criteria: criteria }});
    }

    public getServiceProviderFileGroupingLookupById(staticServiceProviderFileGroupingId: number): AxiosPromise<Model.PlainObject<ServiceProviderFileGroupingLookup>> {
        return this.axios.get(`${this.apiPath}/GetServiceProviderFileGroupingLookupById`, { params: { staticServiceProviderFileGroupingId } });
    }
    // getTradeDistributionTypes
    public getTradeDistributionTypes(): AxiosPromise<Array<Model.PlainObject<TradeDistributionTypeLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTradeDistributionTypes`);
    }

    public getTradeSftpTypes(): AxiosPromise<Array<Model.PlainObject<TradeSftpTypeLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTradeSftpTypes`);
    }

    public executeLBVH(staticServiceProviderFileGroupingId: number): AxiosPromise<ExecuteLBVHCommandResult> {
        return this.axios.post(`${this.apiPath}/ExecuteLBVH`, null, { params: { staticServiceProviderFileGroupingId } });
    }
}